import React, { useState } from "react";
import styles from "./ParkingPage.module.css";
import { ParkingLocation } from "./types";
import { mockParkingLocations } from "./mockData";

export default function ParkingPage() {
  const [parkingLocations, setParkingLocations] = useState<ParkingLocation[]>(mockParkingLocations);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ParkingLocation | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const filteredLocations = parkingLocations.filter(location => 
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (location.businessModel && location.businessModel.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleCreate = () => {
    setIsCreating(true);
    setEditingLocation({
      id: "",
      name: "",
      address: "",
      businessModel: "",
      city: "",
      state: "",
      country: "United States",
      zipCode: ""
    });
    setShowDrawer(true);
  };

  const handleEdit = (location: ParkingLocation) => {
    setIsCreating(false);
    setEditingLocation({ ...location });
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingLocation(null);
    setIsCreating(false);
  };

  const handleSave = () => {
    if (!editingLocation) return;

    if (isCreating) {
      const newLocation = {
        ...editingLocation,
        id: Date.now().toString()
      };
      setParkingLocations([...parkingLocations, newLocation]);
    } else {
      setParkingLocations(parkingLocations.map(loc => 
        loc.id === editingLocation.id ? editingLocation : loc
      ));
    }

    handleCloseDrawer();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this parking location?")) {
      setParkingLocations(parkingLocations.filter(loc => loc.id !== id));
    }
  };

  const updateField = (field: keyof ParkingLocation, value: any) => {
    if (editingLocation) {
      setEditingLocation({ ...editingLocation, [field]: value });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Parking</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createBtn} onClick={handleCreate}>
            + Create
          </button>
        </div>
      </div>

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by address or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th></th>
              <th>NAME</th>
              <th>ADDRESS</th>
              <th>BUSINESS MODEL</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredLocations.map((location) => (
              <tr key={location.id}>
                <td>
                  <div className={styles.locationIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                </td>
                <td className={styles.locationName}>{location.name}</td>
                <td>{location.address}</td>
                <td>{location.businessModel || "-"}</td>
                <td>
                  <div className={styles.actions}>
                    <button 
                      className={styles.actionBtn} 
                      title="Edit"
                      onClick={() => handleEdit(location)}
                    >
                      Edit
                    </button>
                    <button 
                      className={styles.actionBtn} 
                      title="Delete"
                      onClick={() => handleDelete(location.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {filteredLocations.length} of {parkingLocations.length} locations
        </span>
      </div>

      {showDrawer && (
        <>
          <div className={styles.drawerOverlay} onClick={handleCloseDrawer} />
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>
                {isCreating ? "New Parking" : "Edit Parking"}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseDrawer}>
                &times;
              </button>
            </div>

            <div className={styles.drawerContent}>
              {editingLocation && (
                <form className={styles.form}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Parking address</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Enter full address"
                      value={editingLocation.address}
                      onChange={(e) => updateField("address", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Parking name</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Example: Downtown Hub"
                      value={editingLocation.name}
                      onChange={(e) => updateField("name", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Business Model</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Example: Miami Operations"
                      value={editingLocation.businessModel || ""}
                      onChange={(e) => updateField("businessModel", e.target.value)}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>City</label>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="City"
                        value={editingLocation.city || ""}
                        onChange={(e) => updateField("city", e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>State</label>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="State (e.g., FL)"
                        value={editingLocation.state || ""}
                        onChange={(e) => updateField("state", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Zip Code</label>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="ZIP"
                        value={editingLocation.zipCode || ""}
                        onChange={(e) => updateField("zipCode", e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>Country</label>
                      <input
                        type="text"
                        className={styles.input}
                        value={editingLocation.country || "United States"}
                        onChange={(e) => updateField("country", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Capacity (Optional)</label>
                    <input
                      type="number"
                      className={styles.input}
                      placeholder="Number of vehicles"
                      value={editingLocation.capacity || ""}
                      onChange={(e) => updateField("capacity", parseInt(e.target.value) || undefined)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Notes (Optional)</label>
                    <textarea
                      className={styles.textarea}
                      placeholder="Add any additional notes"
                      rows={4}
                      value={editingLocation.notes || ""}
                      onChange={(e) => updateField("notes", e.target.value)}
                    />
                  </div>
                </form>
              )}
            </div>

            <div className={styles.drawerFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave}>
                {isCreating ? "Add Parking" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}